import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Container, IconButton, Paper,
  Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tooltip, Typography,
} from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";

import DialogConfirmation from "../../../generic/DialogConfirmation";
import ReviewStatusChip from "../../../generic/ReviewStatusChip";
import { getContractLegal } from "../../../../services/backend/ContractLegalService";
import {
  createPaymentRequest,
  deletePaymentRequest,
  getPaymentRequests,
} from "../../../../services/backend/PaymentRequestLegalService";
import { getContractConfig } from "../../../../services/backend/ContractConfigService";
import { PAYMENT_REQUEST_STATUS, PAYMENT_REQUEST_STATUS_UI } from "../../../../constants";
import { getApprovalBlockReason, resolvePaymentRequestDocs } from "./paymentRequestLogic";
import {
  getRequestReviewDisplayStatus,
  getRequestReviewProgress,
} from "./paymentReviewUi";

// Lista de Solicitudes de Pago de un contrato jurídico.
//
// Envío libre: la empresa puede crear una solicitud cuando quiera y tantas
// veces como facturas emita, sin atadura al plan de pagos ni al mes calendario
// (daily 2026-08-13). Por eso no hay periodo, ni límite por mes, ni celdas
// vacías esperando un mes que no llegó.

const currency = (value) =>
  "$" + (Number(value) || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 });

const formatDate = (value) => (value ? new Date(value).toLocaleDateString("es-CO") : "—");

const PaymentRequestList = () => {
  const { contractCode } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);

  const [requests, setRequests] = useState(null);
  const [contract, setContract] = useState(null);
  // El catálogo se necesita para leer el estado de revisión de cada solicitud:
  // los obligatorios congelados al presentar manda, pero las solicitudes
  // previas a C7 no los tienen y caen al catálogo vigente.
  const [catalog, setCatalog] = useState([]);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const notify = useCallback(
    (message, severity = "error") => setSnack({ open: true, message, severity }),
    []
  );

  const load = useCallback(async () => {
    const [list, contractData, config] = await Promise.all([
      getPaymentRequests(contractCode, user),
      getContractLegal(contractCode, user),
      getContractConfig(),
    ]);
    setRequests(list || []);
    setContract(contractData);
    setCatalog(resolvePaymentRequestDocs(config?.paymentRequestDocs));
  }, [contractCode]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Solo la empresa dueña crea y elimina; el backend lo revalida.
  const isEntityUser = user?.userType === "ENTITY";

  const handleCreate = async () => {
    setCreating(true);
    try {
      const result = await createPaymentRequest(contractCode, user);
      if (!result.ok) {
        notify(result.message || "No fue posible crear la solicitud");
        return;
      }
      navigate(`/contracts/legal/${contractCode}/payments/${result.data.requestId}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (requestId) => {
    const result = await deletePaymentRequest(requestId, user);
    if (!result.ok) {
      notify(result.message || "No fue posible eliminar la solicitud");
      return;
    }
    notify("Solicitud eliminada", "success");
    load();
  };

  if (requests === null) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 6 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }} flexWrap="wrap">
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/contracts")}>
          Volver
        </Button>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Solicitudes de pago
        </Typography>
        {isEntityUser && (
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
            disabled={creating}
            onClick={handleCreate}
          >
            Nueva solicitud
          </Button>
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Contrato {contractCode}
        {contract?.entity?.legalName ? ` · ${contract.entity.legalName}` : ""}
      </Typography>

      {isEntityUser && (
        <Alert severity="info" sx={{ my: 2 }}>
          Presenta una solicitud por cada factura, cuando lo necesites. No hay un
          límite de solicitudes por mes.
        </Alert>
      )}

      {requests.length === 0 ? (
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            {isEntityUser
              ? "Todavía no has presentado solicitudes de pago para este contrato."
              : "La empresa no ha presentado solicitudes de pago para este contrato."}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Creada</TableCell>
                <TableCell>Enviada</TableCell>
                <TableCell align="right">Valor facturado</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Revisión</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => {
                const statusUi = PAYMENT_REQUEST_STATUS_UI[request.status] || {};
                const isDraft = request.status === PAYMENT_REQUEST_STATUS.DRAFT;
                // Avance de la revisión. El color/icono/etiqueta sale del mapa
                // único (REVIEW_STATUS_UI vía ReviewStatusChip) y el detalle de
                // lo que falta, del helper compartido con el servidor.
                const reviewStatus = getRequestReviewDisplayStatus(request, catalog);
                const progress = getRequestReviewProgress(request, catalog);
                return (
                  <TableRow key={request.requestId} hover>
                    <TableCell>{request.seq}</TableCell>
                    <TableCell>{formatDate(request.createdAt)}</TableCell>
                    <TableCell>{formatDate(request.submittedAt)}</TableCell>
                    <TableCell align="right">
                      {request.invoiceAmount ? currency(request.invoiceAmount) : "—"}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          size="small"
                          label={statusUi.label || request.status}
                          color={statusUi.color}
                          sx={statusUi.sx}
                          icon={statusUi.Icon ? <statusUi.Icon /> : undefined}
                        />
                        {/* Devolución dentro del recorrido financiero (acta #3):
                            no cambia el color del estado, se señala aparte. */}
                        {Array.isArray(request.financial?.returns) && request.financial.returns.length > 0 && (
                          <Tooltip
                            title={`Devuelta a Causación desde ${
                              request.financial.returns[request.financial.returns.length - 1].fromLabel
                            }`}
                          >
                            <AssignmentReturnIcon fontSize="small" color="warning" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {isDraft ? (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      ) : (
                        <Stack spacing={0.5} alignItems="flex-start">
                          <ReviewStatusChip
                            status={reviewStatus}
                            tooltip={getApprovalBlockReason({ request, catalog })}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {progress.reviewed}/{progress.total} revisados
                            {progress.returned > 0 ? ` · ${progress.returned} devuelto(s)` : ""}
                          </Typography>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={isDraft ? "Continuar" : "Ver"}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/contracts/legal/${contractCode}/payments/${request.requestId}`)
                          }
                        >
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isEntityUser && isDraft && (
                        <Tooltip title="Eliminar borrador">
                          <IconButton
                            size="small"
                            onClick={() => setConfirmDelete(request)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DialogConfirmation
        confirmInfo={{
          open: !!confirmDelete,
          title: "Eliminar solicitud",
          confirm: "Eliminar",
          data: confirmDelete,
        }}
        content="Se eliminará este borrador y no podrás recuperarlo."
        handleCloseDialog={(confirmed, data) => {
          setConfirmDelete(null);
          if (confirmed && data) handleDelete(data.requestId);
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PaymentRequestList;
